import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Gauge, RefreshCw, Volume2, CheckCircle2 } from "lucide-react";
import { setDeviceGender, speakDevice } from "@/lib/deviceVoice";

function UsageBar({ m, limit }) {
  const pct = Math.min(100, Math.round((m.count / limit) * 100));
  const color = pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-600";
  return (
    <div data-testid={`gemini-usage-${m.label === "Voix principale" ? "primary" : "fallback"}`}>
      <div className="flex justify-between text-sm mb-1">
        <span className="font-semibold">{m.label}</span>
        <span className="tabular-nums" data-testid="gemini-usage-count">{m.count} / {limit}</span>
      </div>
      <div className="h-3 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color} transition-[width] duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function DeviceVoiceChoice() {
  const [gender, setGender] = useState(null);
  useEffect(() => { api.get("/settings/device-voice").then(({ data }) => setGender(data.gender)).catch(() => setGender("female")); }, []);
  const choose = async (g) => {
    try {
      await api.put("/admin/device-voice", { gender: g });
      setGender(g); setDeviceGender(g);
      speakDevice("Bonjour, je suis la voix de la tablette.", { gender: g });
      toast.success(g === "male" ? "Voix d'homme choisie pour tout l'établissement" : "Voix de femme choisie pour tout l'établissement");
    } catch { toast.error("Enregistrement impossible."); }
  };
  return (
    <div className="mt-6 pt-6 border-t border-border/60">
      <p className="font-semibold mb-1">Voix de la tablette (relais)</p>
      <p className="text-sm text-muted-foreground mb-3">Utilisée quand la voix Gemini n'est pas disponible, pour tout l'établissement.</p>
      <div className="flex flex-wrap gap-2">
        {[["female", "Voix de femme"], ["male", "Voix d'homme"]].map(([g, label]) => (
          <Button key={g} variant={gender === g ? "default" : "outline"} onClick={() => choose(g)} data-testid={`device-voice-${g}`} aria-pressed={gender === g}>
            {label}
          </Button>
        ))}
        <Button variant="ghost" onClick={() => speakDevice("Bonjour, je suis la voix de la tablette.")} data-testid="device-voice-test">
          <Volume2 className="w-4 h-4 mr-2" /> Écouter
        </Button>
      </div>
    </div>
  );
}

function Remaining({ left }) {
  if (!left) return null;
  if (left.done) {
    return (
      <div className="mt-5 rounded-2xl bg-emerald-50 border-2 border-emerald-200 p-4 flex items-center gap-3 text-emerald-900" data-testid="voices-remaining-done">
        <CheckCircle2 className="w-6 h-6 shrink-0" /> <span className="font-semibold">Toutes les voix sont refaites avec Gemini.</span>
      </div>
    );
  }
  const total = left.gardiens + left.words + left.stories;
  return (
    <div className="mt-5 rounded-2xl bg-amber-50 border-2 border-amber-200 p-4 text-amber-900" data-testid="voices-remaining">
      <p className="font-semibold mb-1" data-testid="voices-remaining-total">Encore {total} voix à refaire</p>
      <p className="text-sm" data-testid="voices-remaining-detail">
        {left.gardiens} voix de Gardiens · {left.words} mots de prononciation · {left.stories} conte(s)
      </p>
    </div>
  );
}

export default function VoiceQuotaCard() {
  const [data, setData] = useState(null);
  const [left, setLeft] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/gemini-usage").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/voices-remaining").then((r) => setLeft(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const resume = async () => {
    try { await api.post("/admin/voices-resume"); toast.success("Reprise des voix manquantes lancée."); setTimeout(load, 2000); }
    catch { toast.error("Reprise impossible."); }
  };
  return (
    <Card className="mb-8 rounded-3xl" data-testid="voice-quota-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1"><Gauge className="w-5 h-5 text-emerald-700" /><h2 className="text-lg font-semibold">Voix Gemini du jour</h2></div>
        {!data ? <Loader2 className="w-5 h-5 animate-spin mt-3" /> : (
          <>
            <p className="text-sm text-muted-foreground mb-4">Compteur remis à zéro chaque jour à {data.reset_at} (heure de Paris). Les voix manquantes sont reprises automatiquement chaque nuit.</p>
            <div className="space-y-4">{data.models.map((m) => <UsageBar key={m.model} m={m} limit={data.limit} />)}</div>
            <Remaining left={left} />
            {!left?.done && <Button variant="outline" className="mt-4" onClick={resume} disabled={data.resume_running} data-testid="voices-resume-button">
              <RefreshCw className={`w-4 h-4 mr-2 ${data.resume_running ? "animate-spin" : ""}`} /> {data.resume_running ? "Reprise en cours…" : "Reprendre maintenant"}
            </Button>}
          </>
        )}
        <DeviceVoiceChoice />
      </CardContent>
    </Card>
  );
}
